import { Directive, EventEmitter, HostListener, Output, inject } from '@angular/core';
import { DeviceService } from 'src/services/device.service';

@Directive({
  selector: '[liberroSwipe]',
  standalone: false,
})
export class LiberroSwipeDirective {
  private deviceService = inject(DeviceService);

  private touchStartX: number | null = null;
  private touchStartY: number | null = null;
  private touchStartTime: number | null = null;
  private isHorizontalSwipe = false;

  private readonly swipeThresholdPx = 30;
  private readonly swipeVerticalTolerancePx = 30;
  private readonly minSwipeVelocityPxPerMs = 0.3;

  @Output() public swipeLeft = new EventEmitter<void>();
  @Output() public swipeRight = new EventEmitter<void>();
  @Output() public swipeCancel = new EventEmitter<void>();

  @HostListener('touchstart', ['$event'])
  public onTouchStart(event: TouchEvent): void {
    if (!event.touches.length) {
      return;
    }

    const touch = event.touches[0];
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.touchStartTime = Date.now();
    this.isHorizontalSwipe = false;
  }

  @HostListener('touchmove', ['$event'])
  public onTouchMove(event: TouchEvent): void {
    if (this.touchStartX === null || !event.touches.length) {
      return;
    }

    const touch = event.touches[0];
    const deltaX = touch.clientX - this.touchStartX;
    const deltaY = touch.clientY - (this.touchStartY ?? 0);

    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
      this.isHorizontalSwipe = true;

      if (this.deviceService.isNativePlatform) {
        event.preventDefault();
      }
    }

    if (Math.abs(deltaY) > this.swipeVerticalTolerancePx && !this.isHorizontalSwipe) {
      return;
    }
  }

  @HostListener('touchend', ['$event'])
  public onTouchEnd(event: TouchEvent): void {
    if (this.touchStartX === null || !event.changedTouches.length) {
      this.resetTouchState();
      return;
    }

    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - this.touchStartX;
    const deltaY = touch.clientY - (this.touchStartY ?? 0);
    const deltaTime = this.touchStartTime ? Date.now() - this.touchStartTime : 0;
    const velocity = deltaTime > 0 ? Math.abs(deltaX) / deltaTime : 0;

    const isQuickSwipe = velocity > this.minSwipeVelocityPxPerMs && Math.abs(deltaX) > 20;
    const isSlowSwipe = Math.abs(deltaX) >= this.swipeThresholdPx;
    const isHorizontal =
      this.isHorizontalSwipe ||
      (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaY) <= this.swipeVerticalTolerancePx);

    if (!isHorizontal || (!isQuickSwipe && !isSlowSwipe)) {
      this.swipeCancel.emit();
      this.resetTouchState();
      return;
    }

    if (deltaX < 0) {
      this.swipeLeft.emit();
    } else if (deltaX > 0) {
      this.swipeRight.emit();
    } else {
      this.swipeCancel.emit();
    }

    this.resetTouchState();
  }

  private resetTouchState(): void {
    this.touchStartX = null;
    this.touchStartY = null;
    this.touchStartTime = null;
    this.isHorizontalSwipe = false;
  }
}

